/**
 * Action: Request for dismissal (dismiss a civil action, or part of it, after a settlement
 * or voluntarily). Statewide base with the court layer.
 *
 * Every rule cited here comes from the files in data/. Court specifics come from
 * src/courts/ through courtDetail(); anything the files do not cover is listed under
 * "verify" rather than asserted.
 */
import { courtDetail } from "../lib/courtlayer.js";
import { checklist, fmt, fmtISO, lastDayAfter, parse, todayISO } from "../lib/civil.js";

export const id = "request-for-dismissal";
export const statewide = true;
// The statewide forms its checklist can link, which a case offers when this action is added to it.
export const forms = ["ca-civ-110", "ca-civ-120", "ca-cm-200"];

const REASON_TXT = {
  settled: "The entire case has settled",
  voluntary: "A voluntary dismissal (not a settlement of the entire case)",
};
const SCOPE_TXT = {
  entire: "The entire action, as to all parties and all causes of action",
  complaint: "The complaint, as to all defendants and causes of action in it",
  cross: "A cross-complaint",
  partial: "Only some parties or some causes of action",
};
// CIV-110 item 1b for each scope.
const SCOPE_ITEM = {
  entire: "1b(5), entire action of all parties and all causes of action",
  complaint: "1b(1), complaint",
  cross: "1b(3) or 1b(4), naming the cross-complaint by who filed it and when",
  partial: "1b(6), other, identifying the parties, causes of action or cross-complaints dismissed",
};
const RECOVERY_TXT = {
  none: "Nothing of value",
  under: "Less than $10,000 in value",
  over: "$10,000 or more in value",
};
const yesNo = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const yn = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "");

const settled = (a) => a.reason === "settled";
const conditional = (a) => settled(a) && a.cond === "conditional";
// A settlement of the entire case is dismissed as a whole (CRC 3.1385(b)).
const scopeOf = (a) => (settled(a) ? "entire" : a.scope);
const retains = (a) => settled(a) && a.prejudice === "without" && a.retain === "yes";
const isClass = (a) => a.classAction === "yes";

/* ------------------------------------------------------------------ steps */
export const steps = [
  {
    id: "why",
    title: "Why the case is being dismissed",
    intro: "A settlement of the entire case brings its own notice and deadline. The wizard doesn't ask for names or case details.",
    fields: [
      {
        k: "reason", type: "radio", label: "Why is the dismissal being filed?", req: true,
        opts: [
          { v: "settled", l: REASON_TXT.settled, h: "Each plaintiff or other party seeking affirmative relief must file a notice of settlement and then a request for dismissal of the entire case (CRC 3.1385)." },
          { v: "voluntary", l: REASON_TXT.voluntary },
        ],
      },
      {
        k: "cond", type: "radio", label: "Is the settlement conditional?", req: true,
        show: settled,
        opts: [
          { v: "unconditional", l: "Unconditional" },
          { v: "conditional", l: "Conditional", h: "The settlement agreement conditions dismissal on terms that are not to be performed within 45 days of the settlement, such as installment payments (CRC 3.1385(c)(1))." },
        ],
      },
      {
        k: "settleDate", type: "date", label: "Date of the settlement", req: true,
        show: (a) => settled(a) && a.cond === "unconditional",
      },
      {
        k: "dismissBy", type: "date", label: "Date the notice of settlement gives (or will give) for filing the dismissal", req: true,
        show: conditional,
        hint: "CM-200, item 1b.",
      },
      {
        k: "scope", type: "radio", label: "What is being dismissed?", req: true,
        show: (a) => a.reason === "voluntary",
        opts: Object.entries(SCOPE_TXT).map(([v, l]) => ({ v, l })),
      },
    ],
  },
  {
    id: "dismissal",
    title: "The dismissal",
    intro: "These answers pick the boxes on the request and whose consent it needs.",
    fields: [
      {
        k: "prejudice", type: "radio", label: "With or without prejudice?", req: true,
        opts: [{ v: "with", l: "With prejudice" }, { v: "without", l: "Without prejudice" }],
      },
      {
        k: "retain", type: "radio", label: "Will the court be asked to retain jurisdiction to enforce the settlement (CCP 664.6)?", req: true,
        show: (a) => settled(a) && a.prejudice === "without",
        hint: "The court may dismiss without prejudice as to the settling parties and retain jurisdiction until the settlement is performed in full, if the parties stipulate (CCP 664.6(a)).",
        opts: yesNo,
      },
      {
        k: "trial", type: "radio", label: "Has trial actually begun?", req: true,
        hint: "Trial begins at the start of the first opening statement or argument, or if there is none, when the first witness is sworn or evidence is introduced (CCP 581(a)(6)).",
        opts: yesNo,
      },
      {
        k: "cross", type: "radio", label: "Is a cross-complaint seeking affirmative relief on file?", req: true,
        show: (a) => scopeOf(a) && scopeOf(a) !== "cross",
        opts: yesNo,
      },
      {
        k: "classAction", type: "radio", label: "Is it a class action?", req: true,
        hint: "A case brought as a class action under Civil Code 1750 et seq. or CCP 382, until the court finds it isn't maintainable as one (CRC 3.760(a)).",
        opts: yesNo,
      },
    ],
  },
  {
    id: "fees",
    title: "Waived court fees",
    intro: (a, court) => `The request says whether the court waived fees for any party. The clerk at ${court.name} can tell you if you don't know (CIV-110, item 2).`,
    fields: [
      { k: "waived", type: "radio", label: "Did the court waive court fees and costs for any party in this case?", req: true, opts: yesNo },
      {
        k: "recovery", type: "radio", label: "What is the party whose fees were waived recovering by this action?", req: true,
        show: (a) => a.waived === "yes",
        hint: "By settlement, compromise, arbitration award, mediation settlement or other means (GC 68637(c)).",
        opts: Object.entries(RECOVERY_TXT).map(([v, l]) => ({ v, l })),
      },
    ],
  },
];

/** Shown on the review step. */
export function summary(a, court) {
  const rows = [];
  const add = (l, v) => v && rows.push([l, v]);
  add("Court", court.name);
  add("Reason", REASON_TXT[a.reason]);
  if (settled(a)) add("Settlement", a.cond === "conditional" ? "Conditional" : a.cond ? "Unconditional" : "");
  add("Date of settlement", settled(a) && a.cond === "unconditional" ? fmtISO(a.settleDate) : "");
  add("Dismissal date in the notice", conditional(a) ? fmtISO(a.dismissBy) : "");
  add("Dismissing", SCOPE_TXT[scopeOf(a)]);
  add("Prejudice", a.prejudice === "with" ? "With prejudice" : a.prejudice === "without" ? "Without prejudice" : "");
  if (settled(a) && a.prejudice === "without") add("Court retains jurisdiction (CCP 664.6)", yn(a.retain));
  add("Trial has begun", yn(a.trial));
  if (scopeOf(a) !== "cross") add("Cross-complaint seeking affirmative relief", yn(a.cross));
  add("Class action", yn(a.classAction));
  add("Fees waived for a party", yn(a.waived));
  if (a.waived === "yes") add("That party is recovering", RECOVERY_TXT[a.recovery]);
  return rows;
}

/* ------------------------------------------------------------------ checklist */
export function build(a, court) {
  const c = checklist();
  const scope = scopeOf(a);
  const trial = a.trial === "yes";
  const cross = scope !== "cross" && a.cross === "yes";
  const klass = isClass(a);
  const retain = retains(a);
  const withoutAfterTrial = trial && a.prejudice === "without";

  // CRC 3.1385: 45 days after the settlement, or after the dismissal date in a conditional notice.
  let due = null;
  let extendBy = null;
  if (settled(a)) {
    const from = (conditional(a) ? a.dismissBy : a.settleDate) || todayISO();
    due = { from, ...lastDayAfter(court, from, 45) };
    extendBy = court.calendar.addCourtDays(due.date, -5);
  }
  const dueMoved = due && due.moved ? `The 45th day, ${fmt(due.raw)}, isn't a court day, so the period runs to the next one (CCP 12a).` : "";

  /* ---- notice of settlement ---- */
  if (settled(a)) {
    const p1 = c.section("notice", "Phase 1. Notice of settlement");
    c.item(p1, "cm200", conditional(a)
      ? `File a Notice of Settlement of Entire Case (CM-200) immediately, checking item 1b (conditional) and giving the date by which the dismissal will be filed (${fmtISO(a.dismissBy)}). Serve it on all parties and on any arbitrator or other court-connected ADR neutral in the case.`
      : `File a Notice of Settlement of Entire Case (CM-200) immediately, checking item 1a (unconditional) with the date of settlement (${fmtISO(a.settleDate)}). Serve it on all parties and on any arbitrator or other court-connected ADR neutral in the case.`,
    conditional(a) ? "CRC 3.1385(a)(1), (c)(1)" : "CRC 3.1385(a)(1)", "Each plaintiff or other party seeking affirmative relief files one. The proof of service by mail on page 2 is signed by someone who is not a party.", ["ca-cm-200"]);
    c.item(p1, "fee", "There is no fee for filing the notice of settlement.", "Fee Schedule item 71");
    c.item(p1, "oral", "If a hearing, conference or trial is scheduled within 10 days, also give oral notice of the settlement to all parties and any arbitrator or ADR neutral.", "CRC 3.1385(a)(1)");
    c.item(p1, "neutral", "Notify any arbitrator or court-connected ADR neutral at least 2 days before a scheduled hearing or session, or the court may order the party to pay for the scheduled time.", "CRC 3.1385(a)(2)");
    if (conditional(a)) c.item(p1, "vacate", "On the filing of the notice of conditional settlement, the court vacates hearings that need a party's appearance and sets none earlier than 45 days after the dismissal date in the notice, unless a party asks. Hearings on sanctions OSCs and good faith settlement determinations (CCP 877.6) may stay on calendar.", "CRC 3.1385(c)(3)");
    if (!trial) {
      const d = courtDetail(court, "rules.settlementBeforeTrial", "");
      if (d.known) c.item(p1, "trial", d.text, d.cite);
    }
    const arb = courtDetail(court, "rules.arbitrationSettled", "");
    if (arb.known) c.item(p1, "arb", arb.text, arb.cite);
    c.item(p1, "minor", "If the settlement compromises the claim of a minor or a person with a disability, the court won't hold the dismissal OSC before its hearing to approve the settlement, as long as the papers seeking approval are filed.", "CRC 3.1385(d)");
    if (klass) c.item(p1, "classSettle", "A settlement of a class action, of a cause of action in it, or as to a party needs the court's approval after a hearing.", "CRC 3.769(a)");
  }

  /* ---- retained jurisdiction ---- */
  if (retain) {
    const p2 = c.section("retain", "Phase 2. Stipulation for the court to retain jurisdiction");
    c.item(p2, "stip", "The court may dismiss the case without prejudice as to the settling parties and retain jurisdiction to enforce the settlement until it is performed in full, if the parties or their counsel stipulate in writing or orally before the court. A writing is signed by a party if signed by the party, its attorney, or (for a party an insurer defends and indemnifies, within policy limits) an agent the insurer authorizes in writing.", "CCP 664.6(a)-(b)");
    c.court(p2, "local", court, "rules.settlementRetention", "local forms or rules for a stipulation and order for the court to retain jurisdiction under CCP 664.6, and when it must be filed",
      { text: "Put the parties' stipulation for the court to retain jurisdiction in writing, signed as CCP 664.6(b) allows, or state it orally before the court.", cite: "CCP 664.6(a)-(b)" });
    c.item(p2, "fee", "Filing a stipulation and order costs $20, unless it is the party's first paper and the first paper fee is paid.", "GC 70617(c)(2); Fee Schedule item 58");
    c.item(p2, "still", "Retaining jurisdiction doesn't change the settlement duties: the notice of settlement and the request for dismissal are still required.", "CRC 3.1385, Advisory Committee Comment");
  }

  /* ---- the request ---- */
  const p3 = c.section("request", "Phase 3. Prepare the request");
  const how = a.prejudice === "with" ? "with prejudice" : "without prejudice";
  if (klass) {
    c.item(p3, "noForm", "Don't use the Request for Dismissal (CIV-110): it may not be used to dismiss a class action or any party or cause of action in one.", "CIV-110; CRC 3.760, 3.770");
    c.item(p3, "approval", `Ask the court to approve the dismissal (${how}). Dismissing the entire class action, or any party or cause of action in it, needs the court's approval, and the court can't grant it after entering judgment following final approval of a settlement.`, "CRC 3.770(a); CCP 581(k)");
    c.item(p3, "decl", "Support the request with a declaration of the facts relied on, stating clearly whether any consideration, direct or indirect, is being given for the dismissal and describing it in detail.", "CRC 3.770(a)");
    c.item(p3, "notice", "If the class was certified and notice of the action was given, notice of the dismissal must be given to the class as the court directs. If certification hasn't been ruled on, the court may direct notice of the proposed dismissal or dismiss without it on finding the class won't be prejudiced.", "CRC 3.770(c); CCP 581(k)");
    c.item(p3, "hearing", "The court may grant the request without a hearing. If it tentatively disapproves, any party may ask for a hearing within 15 calendar days of service of the notice of tentative disapproval; otherwise the request is deemed denied.", "CRC 3.770(b)");
    c.verify(`The downloaded files have no Judicial Council form for dismissing a class action. Check the local rules of ${court.name} for how to present the request and the declaration, and whether a motion fee applies.`);
  } else {
    const box1a = a.prejudice === "with" ? "1a(1), with prejudice" : retain ? "1a(3), without prejudice and with the court retaining jurisdiction (CCP 664.6)" : "1a(2), without prejudice";
    c.item(p3, "civ110", `Complete the Request for Dismissal (CIV-110): item ${box1a}; item ${SCOPE_ITEM[scope]}.`, scope === "cross" ? "CCP 581(a)(2), (a)(5), (c)" : "CCP 581(b)-(c)", "", ["ca-civ-110"]);
    c.item(p3, "item2", `Item 2: check that the court ${a.waived === "yes" ? "did" : "did not"} waive court fees and costs for a party.`, "CIV-110, item 2; GC 68637(c)");
  }
  c.item(p3, "sign", "The attorney of record signs the request. If a represented party applies on its own, it needs its attorney's written consent, or a court order after notice to the attorney.", "CCP 581(j)");
  if (trial) {
    c.item(p3, "trial", a.prejudice === "with"
      ? "Trial has begun, so the plaintiff can no longer dismiss by request to the clerk. On the plaintiff's request, the court dismisses with prejudice."
      : "Trial has begun, so the plaintiff can no longer dismiss by request to the clerk, and on its request the court dismisses with prejudice. A dismissal without prejudice needs the consent of all affected parties to the trial, or a court order on a showing of good cause.",
    "CCP 581(b)(1), (c), (e)");
    if (withoutAfterTrial) c.item(p3, "consent", `Get the written consent of every affected party to dismissal without prejudice${klass ? "" : " (item 3)"}, or prepare a showing of good cause for the court.`, "CCP 581(b)(2), (e)");
    if (!klass) c.verify(`Whether ${court.name} takes a request for dismissal after trial has begun on CIV-110 or only by request to the trial judge is not in the downloaded files.`);
  } else if (!klass) {
    c.item(p3, "before", "Before trial begins, the plaintiff may dismiss by written request to the clerk, with or without prejudice, on payment of any costs.", "CCP 581(b)(1), (c)");
  }
  if (cross) {
    c.item(p3, "cross", `A cross-complaint seeking affirmative relief is on file, so the action can't be dismissed on the plaintiff's request alone. ${klass ? "Get the written consent of all other parties, including the cross-complainant." : "The cross-complainant's attorney signs the consent in item 3 where CCP 581(i) or (j) requires it; otherwise dismiss with the written consent of all other parties."}`, klass ? "CCP 581(b)(2), (i)" : "CCP 581(b)(2), (i); CIV-110, item 3");
    if (scope === "complaint" || scope === "partial") c.verify("CCP 581(c) lets a plaintiff dismiss its complaint, or any cause of action, before trial, while 581(i) bars a dismissal of the action on the plaintiff's request when a cross-complaint seeks affirmative relief. The downloaded files don't say whether dismissing only the complaint needs the cross-complainant's consent.");
    if (scope === "entire") c.item(p3, "crossToo", "Dismissing the entire action includes the cross-complaint, so the cross-complainant must consent.", klass ? "CCP 581(b)(2)" : "CIV-110, items 1b(5), 3; CCP 581(b)(2)");
  }
  if (!klass) {
    if (retain) c.item(p3, "allSign", "With item 1a(3) checked, all parties must sign the consent in item 3. Use MC-025 or a separate page for more signatures.", "CIV-110, item 3");
    c.item(p3, "conformed", "Give the clerk a way to return a conformed copy (for example, a stamped, self-addressed envelope for a paper filing). The clerk won't return one otherwise.", "CIV-110");
  }
  if (settled(a)) c.item(p3, "serve", "Serve the request for dismissal on all parties as well as filing it.", "CRC 3.1385(b)");

  /* ---- fee waiver ---- */
  if (a.waived === "yes") {
    const p4 = c.section("waiver", "Phase 4. Waived court fees");
    if (!klass) c.item(p4, "decl", "Complete the Declaration Concerning Waived Court Fees on page 2 of CIV-110: the party whose fees were waived and what it is recovering, signed under penalty of perjury.", "CIV-110, page 2; GC 68637(c)(4)", "", ["ca-civ-110"]);
    if (a.recovery === "over") {
      c.item(p4, "lien", "The court has a lien on the recovery for all fees and costs initially waived. They must be paid to the court out of the recovery before the party receives anything of value.", "GC 68637(c)(1)-(2)");
      c.item(p4, "amount", "Ask the clerk to calculate the total fees and costs waived as of the date of the request.", "CRC 3.57");
      c.item(p4, "paid", `${klass ? "With the request, declare under penalty of perjury" : "Check item 2c and complete item 3, declaring"} whether all waived fees and costs have been paid. The court may refuse to dismiss until the lien is satisfied.`, klass ? "GC 68637(c)(4)" : "CIV-110, page 2; GC 68637(c)(4)");
    } else {
      const what = a.recovery === "none" ? "is not recovering anything of value" : "is recovering less than $10,000 in value";
      c.item(p4, "under", `${klass ? `With the request, declare under penalty of perjury that the party ${what}` : `Check item ${a.recovery === "none" ? "2a" : "2b"}: the party ${what}`}. The court's lien applies only to a recovery of $10,000 or more.`, klass ? "GC 68637(c), (c)(4)" : "CIV-110, page 2; GC 68637(c), (c)(4)");
    }
    c.item(p4, "osc", "A request filed without the declaration can lead to an order to show cause why the lien shouldn't be enforced and the parties made jointly and severally liable for the waived fees.", "GC 68637(c)(5)");
  }

  /* ---- file ---- */
  const p5 = c.section("file", "Phase 5. File the request");
  c.court(p5, "efile", court, "efiling.represented", "local rules on whether represented parties must e-file",
    klass ? { text: "File the request and declaration with the court.", cite: "CRC 3.770(a)" } : { text: "File the request with the clerk, with the papers in the case.", cite: "CCP 581(b)(1)" });
  const title = courtDetail(court, "efiling.documentTitle", "");
  if (title.known) c.item(p5, "title", title.text, title.cite);
  if (retain) {
    const local = courtDetail(court, "rules.settlementRetention", "");
    c.item(p5, "order", local.known
      ? "File the stipulation and proposed order to retain jurisdiction before the request for dismissal is entered."
      : "The court dismisses and retains jurisdiction on the parties' stipulation, so have it signed and filed (or made orally before the court) with the request.",
    local.known ? local.cite : "CCP 664.6(a)", "", local.known ? local.forms : []);
  }
  if (!klass) {
    c.item(p5, "fee", "The Statewide Civil Fee Schedule has no fee item for a request for dismissal, and it isn't a paper requiring a hearing under GC 70617(a).", "Statewide Civil Fee Schedule; GC 70617");
    c.verify(`Confirm with ${court.name} that no fee is charged to file the request for dismissal: the fee schedule doesn't list one, but doesn't say "no fee" either.`);
  }
  if (due) {
    c.item(p5, "due", conditional(a)
      ? `File and serve the request by the date in the notice of settlement (${fmtISO(a.dismissBy)}). If it isn't filed within 45 days after that date (by ${fmt(due.date)}), the court must dismiss the entire case unless good cause is shown.`
      : `File and serve the request for dismissal of the entire case within 45 days after the date of settlement: by ${fmt(due.date)}. Otherwise the court must dismiss the entire case 45 days after it receives the notice of settlement, unless good cause is shown.`,
    conditional(a) ? "CRC 3.1385(c)(2)" : "CRC 3.1385(b)", dueMoved);
    c.item(p5, "more", `If the case can't be dismissed in time, serve and file a notice with a supporting declaration showing good cause and proposing another dismissal date, at least 5 court days before the time runs out: by ${fmt(extendBy)}.`, "CRC 3.1385(e)");
    c.court(p5, "osc", court, "departments.caseManagement", "local rules for the department that hears orders to show cause re dismissal under CRC 3.1385", null);
    const osc = courtDetail(court, "rules.oscPapers", "");
    if (osc.known) c.item(p5, "oscPapers", osc.text, osc.cite);
  }

  /* ---- after ---- */
  const p6 = c.section("after", "Phase 6. After the dismissal is entered");
  c.item(p6, "entered", "A written dismissal is effective when the clerk enters it in the register. A dismissal ordered by the court is a written order signed by the court, and once filed it is a judgment.", "CCP 581d", klass ? "" : "The clerk completes items 4-7 of the request.");
  if (klass) c.item(p6, "notice", "Serve notice of entry of the dismissal on all parties and file it.", "CRC 3.1390", "CRC 3.1390 sets no deadline for the notice. CIV-120 is written for a dismissal entered by the clerk on CIV-110.");
  else c.item(p6, "civ120", "Serve notice of entry of the dismissal on all parties and file it. The Notice of Entry of Dismissal and Proof of Service (CIV-120) attaches a copy of the request as completed by the clerk.", "CRC 3.1390", "CRC 3.1390 sets no deadline for the notice.", ["ca-civ-120"]);
  if (retain) {
    c.item(p6, "later", "After the dismissal, a party can still file motions and other papers about the settlement (including to enforce it if its terms aren't performed) without paying a first appearance fee again.", "CCP 664.6(f)");
    c.item(p6, "withPrejudice", "A party may later file a request for dismissal with prejudice.", "CCP 664.6(g)");
  }

  /* ---- rows, verify ---- */
  c.row("Form", klass ? "No Judicial Council form (class action)" : "Request for Dismissal (CIV-110)", klass ? "CRC 3.770" : "CCP 581; CRC 3.1390");
  c.row("Dismissal", `${a.prejudice === "with" ? "With" : "Without"} prejudice${retain ? ", court retains jurisdiction" : ""}`, retain ? "CCP 664.6" : trial ? "CCP 581(e)" : "CCP 581(b)-(c)");
  if (due) {
    c.row(conditional(a) ? "Court must dismiss if nothing is filed by" : "File the request by", fmt(due.date), `45 days after ${fmtISO(due.from)}${due.moved ? `, moved from ${fmt(due.raw)} to the next court day` : ""} (${conditional(a) ? "CRC 3.1385(c)(2)" : "CRC 3.1385(b)"})`);
    c.row("Ask for more time by", fmt(extendBy), "5 court days before (CRC 3.1385(e))");
    c.dates(court, parse(due.from), due.date);
  }
  if (a.waived === "yes" && a.recovery === "over") c.verify("How the court's lien is paid (to which office, and in what form) is not in the downloaded files.");

  const out = c.result();
  let n = 1;
  out.sections.forEach((s) => { s.title = s.title.replace(/^Phase \d+\./, () => `Phase ${n++}.`); });
  return out;
}
